import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, UserIcon, BuildingIcon, CalendarIcon, HashIcon, FileTextIcon } from 'lucide-react';
import type { SKRRecord } from '../lib/skrStore';
const statusStyles: Record<SKRRecord['status'], string> = {
  Active: 'bg-steca-100 text-steca-700',
  Released: 'bg-slate-100 text-slate-600',
  'On Hold': 'bg-amber-100 text-amber-700'
};
function Field({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return <div className="flex items-start gap-3" data-id="element-67" data-mp="2sqma1033t9wn">
      <div className="mt-0.5 text-steca-500" data-id="element-68" data-mp="2srnicwsqjiao">{icon}</div>
      <div data-id="element-69" data-mp="2ezpgn71csooj">
        <dt className="text-xs font-medium uppercase tracking-wide text-steca-ink/50" data-id="element-70" data-mp="2f0922xq7pv7b">{label}</dt>
        <dd className="text-sm font-semibold text-steca-ink" data-id="element-71" data-mp="2jy0vjw6i9pdm">{value}</dd>
      </div>
    </div>;
}
export function SKRResultCard({
  record
}: {
  record: SKRRecord;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 16
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4,
    ease: 'easeOut'
  }} className="overflow-hidden rounded-2xl border border-steca-200 bg-white shadow-sm" data-id="element-72" data-mp="29y9xjkiqks8s">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-steca-100 bg-steca-50 px-6 py-4" data-id="element-73" data-mp="1e9n5943zkjjr">
        <div className="flex items-center gap-2 text-steca-700" data-id="element-74" data-mp="1o5zu9ool6986">
          <ShieldCheckIcon className="h-5 w-5" data-id="element-75" data-mp="1o3m0rk9k152q" />
          <span className="font-display text-sm font-bold uppercase tracking-wide" data-id="element-76" data-mp="10fck0wr777ig">
            Verified Safe Keeping Receipt
          </span>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusStyles[record.status]}`} data-id="element-77" data-mp="veycrgxf81lh">
          {record.status}
        </span>
      </div>
      <div className="grid gap-5 px-6 py-6 sm:grid-cols-2" data-id="element-78" data-mp="14g69hukagudg">
        <Field icon={<HashIcon className="h-4 w-4" data-id="element-80" data-mp="grdblsk3wcg7" />} label="SKR Code" value={record.code} data-id="element-79" data-mp="grmxb3y4bxg3" />
        <Field icon={<UserIcon className="h-4 w-4" data-id="element-82" data-mp="crct9wtvzh3v" />} label="Holder" value={record.holderName} data-id="element-81" data-mp="cr33oacr7byp" />
        <Field icon={<FileTextIcon className="h-4 w-4" data-id="element-84" data-mp="2bu9ycuc9d5w1" />} label="Account Reference" value={record.accountRef} data-id="element-83" data-mp="2bvus4hqr0ksu" />
        <Field icon={<BuildingIcon className="h-4 w-4" data-id="element-86" data-mp="2gs1pvn8zqpzp" />} label="Facility" value={record.facility} data-id="element-85" data-mp="2gsl7t3yn5edq" />
        <Field icon={<CalendarIcon className="h-4 w-4" data-id="element-88" data-mp="2qkh6oh8jx8j0" />} label="Issue Date" value={record.issueDate} data-id="element-87" data-mp="2qjy0fxs9k8kt" />
        <Field icon={<CalendarIcon className="h-4 w-4" data-id="element-90" data-mp="1syjtmzcxxveg" />} label="Expiry Date" value={record.expiryDate} data-id="element-89" data-mp="1sxqsqf0ny048" />
      </div>
      <div className="px-6 pb-6" data-id="element-91" data-mp="22uwk7od6vt4z">
        <h3 className="mb-3 font-display text-sm font-bold uppercase tracking-wide text-steca-ink/70" data-id="element-92" data-mp="22ttw1o7944sr">
          Stored Items
        </h3>
        <div className="overflow-hidden rounded-xl border border-steca-100" data-id="element-93" data-mp="27rd94ks7qbj4">
          <table className="w-full text-left text-sm" data-id="element-94" data-mp="3e9a98wjoieml">
            <thead className="bg-steca-50 text-xs uppercase tracking-wide text-steca-ink/60" data-id="element-95" data-mp="3j8da04smlyor">
              <tr data-id="element-96" data-mp="17m1ergjlxvc5">
                <th className="px-4 py-2 font-semibold" data-id="element-97" data-mp="17khvri6kh66o">Category</th>
                <th className="px-4 py-2 font-semibold" data-id="element-98" data-mp="12mosoy0k8sam">Description</th>
                <th className="px-4 py-2 font-semibold" data-id="element-99" data-mp="12ojcg903ied4">Qty</th>
                <th className="px-4 py-2 text-right font-semibold" data-id="element-100" data-mp="xuf71u6haee5">Est. Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-steca-100" data-id="element-101" data-mp="swdbi9rv8mls">
              {record.items.map(item => <tr key={item.id} className="align-top" data-id="element-102" data-mp="nyvdvfjattt3">
                  <td className="px-4 py-3" data-id="element-103" data-mp="jvp786s9y0m3">
                    <span className="inline-block rounded-md bg-steca-50 px-2 py-0.5 text-xs font-medium text-steca-700" data-id="element-104" data-mp="jy1oehq7k6y3">
                      {item.category}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-steca-ink" data-id="element-105" data-mp="a040jvc9gc60">{item.description}</td>
                  <td className="px-4 py-3 text-steca-ink/80" data-id="element-106" data-mp="29a3roew9f1bs">{item.quantity}</td>
                  <td className="px-4 py-3 text-right font-semibold text-steca-ink" data-id="element-107" data-mp="24d3k1zjrhqik">
                    {item.estimatedValue}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {record.notes && <div className="mt-4 rounded-xl bg-steca-50 px-4 py-3 text-sm text-steca-ink/80" data-id="element-108" data-mp="s0q140eiq0ma">
            <span className="font-semibold text-steca-700" data-id="element-109" data-mp="wz1cifkgoj6m">Notes: </span>
            {record.notes}
          </div>}
      </div>
    </motion.div>;
}